import React, { useEffect, useState } from 'react';

import Button from '@components/_shared/button';
import { ListingsMap } from '@components/search/listingsMap';
import { SearchBar } from '@components/search/searchBar';
import { withLayoutAndSession } from '@utils/hocs';
import { MarketType } from '@utils/types/city';
import { PageWithProvidersAndLayout } from '@utils/types/nextApp';
import {
  SearchListingsProvider,
  useSearchListings,
} from '@utils/context/searchListings';
import { useToggleBodyScroll } from '@utils/hooks/toggleBodyScroll';
import { getCollectionsByType } from '@api/collections/getCollectionsByType';
import MetaHead from '@components/_shared/metaHead';
import { FeaturedListingType } from '@utils/types/featuredListing';
import { ListingCard } from '@components/search/listingCard';
import { getNeighborhoods } from '@api/neighborhoods/getNeighborhoods';
import { NeighborhoodType } from '@utils/types/neighborhoods';

import styles from './styles.module.scss';

type Props = {
  markets: MarketType[];
  neighborhoods: NeighborhoodType[];
  metaTitle: string;
  metaDescription: string;
  ogImage?: string;
};

type ServerSideProps = {
  props: Props;
};

const SearchPage: PageWithProvidersAndLayout<Props> = (props: Props) => {
  const { markets, neighborhoods, metaTitle, metaDescription, ogImage } = props;
  const { listings, filterParams, loading } = useSearchListings();
  const [listingIDToScrollTo, setListingIDToScrollTo] = useState<string>();
  const [mobileViewMap, setMobileViewMap] = useState(false);
  const [mounted, setMounted] = useState(false);
  const { disableScroll, enableScroll } = useToggleBodyScroll();

  useEffect(() => {
    if (!mounted && listings.length) setMounted(true);
  }, [mounted, listings.length]);

  const handleMobileViewMap = () => {
    if (mobileViewMap) {
      setMobileViewMap(false);
      enableScroll();
    } else {
      setMobileViewMap(true);
      disableScroll();
    }
  };

  const onMarkerClick = (id: string) => {
    setListingIDToScrollTo(id);
  };

  const renderListing = (listing: FeaturedListingType, index: number) => {
    return (
      <ListingCard
        key={index}
        listing={listing}
        dates={filterParams.dates}
        listingIDToScrollTo={listingIDToScrollTo}
      />
    );
  };

  return (
    <>
      <MetaHead
        title={metaTitle}
        description={metaDescription}
        ogImage={ogImage}
      />
      <h1 style={{ display: 'none' }}>Search for Short Term Rentals</h1>

      <main className={styles.container}>
        {/* Hide when mobileViewMap is true to avoid z-index issues */}
        {!mobileViewMap && (
          <SearchBar
            markets={markets}
            neighborhoods={neighborhoods}
            containerStyle={styles.searchBar}
          />
        )}

        <div className={styles.searchContainer}>
          <div className={styles.searchList}>
            {loading ? (
              Array.from({ length: 10 }).map((_, index) => (
                <ListingCard key={index} />
              ))
            ) : listings.length === 0 ? (
              <div className={styles.noItems}>
                <div className={styles.noItems__title}>
                  No available options
                </div>
                <div className={styles.noItems__text}>
                  Adjust your search to 30+ days or reach out to
                  reservations@rovetravel.com
                </div>
              </div>
            ) : (
              listings.map(renderListing)
            )}
          </div>

          <div className={styles.searchMap}>
            {mounted && (
              <ListingsMap
                listings={listings}
                dates={filterParams.dates}
                onMarkerClick={onMarkerClick}
                selectedListing={listings.find(
                  (l) => l.id === listingIDToScrollTo
                )}
                resetListingInfo={() => setListingIDToScrollTo(undefined)}
              />
            )}
          </div>

          <div className={styles.mobileSearchMap}>
            <Button
              onClick={handleMobileViewMap}
              containerStyle={styles.viewMap}
              variant={mobileViewMap ? 'tanOutlined' : 'tanContained'}
            >
              {mobileViewMap ? 'View List' : 'View Map'}
            </Button>

            {mobileViewMap && (
              <div className={styles.mobileMap}>
                <ListingsMap
                  listings={listings}
                  dates={filterParams.dates}
                  onMarkerClick={onMarkerClick}
                  selectedListing={listings.find(
                    (l) => l.id === listingIDToScrollTo
                  )}
                  resetListingInfo={() => setListingIDToScrollTo(undefined)}
                  containerStyle={styles.mobileGoogleMaps}
                />
              </div>
            )}
          </div>
        </div>
      </main>
    </>
  );
};

const SearchPageWithContext: PageWithProvidersAndLayout<Props> = (
  props: Props
) => (
  <SearchListingsProvider>
    <SearchPage {...props} />
  </SearchListingsProvider>
);

export async function getServerSideProps(
  context: any
): Promise<ServerSideProps | undefined> {
  try {
    const neighborhoods = await getNeighborhoods();
    const collections = await getCollectionsByType(['market']);
    const marketNameFromQuery = collections.find(
      ({ slug }) => slug.current === context.query.market
    );
    const marketImage = collections.find(
      ({ slug }) => slug.current === context.query.market
    )?.image?.asset?.url;

    const props: Props = {
      markets: collections.map(({ name, slug }) => ({
        name,
        slug: slug.current,
      })),
      neighborhoods,
      metaTitle: marketNameFromQuery?.searchMetaTitle
        ? marketNameFromQuery.searchMetaTitle
        : 'Search for Short Term Rentals',
      metaDescription: marketNameFromQuery?.searchMetaDescription
        ? marketNameFromQuery.searchMetaDescription
        : '',
    };

    if (marketImage) {
      props.ogImage = marketImage;
    }

    return {
      props,
    };
  } catch (error) {
    console.error(error);
  }
}

export default withLayoutAndSession(SearchPageWithContext, {
  headerProps: { theme: 'white' },
  footerProps: {
    theme: 'white',
    containerStyle: styles.footer,
  },
});
